<template>
  <div>
    <PageHeaderBanner>
      <template #title>
        <span v-if="mode == modeType.MODE_REGISTER">納期新規登録</span>
        <span v-if="mode == modeType.MODE_EDIT">納期変更</span>
      </template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <q-form
      autofocus
      greedy
      @submit.prevent="submit"
      @reset="reset"
      @validation-error="resetErrors"
    >
      <div class="cmd00510-input-form q-gutter-y-sm">
        <div class="row">
          <div class="col">
            <label class="requier">記載No.</label>
            <q-input
              v-model="inputData.described_number"
              outlined
              dense
              style="background: var(--q-white); width: 30%"
              class="input-area"
              :maxlength="9"
              :rules="[
                (val) => isEmpty(val) || $t('validation.required'),
                (val) => isNumeric(val) || $t('validation.numeric_only'),
              ]"
              lazy-rules
              @keydown.enter.prevent
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <label class="requier">基準納期（種別）</label>
            <q-input
              v-model="inputData.standard_delivery_time_code"
              outlined
              dense
              style="background: var(--q-white); width: 30%"
              class="input-area"
              :maxlength="20"
              :rules="[(val) => isEmpty(val) || $t('validation.required')]"
              lazy-rules
              @keydown.enter.prevent
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <label class="requier">基準納期（日数）</label>
            <q-input
              v-model="inputData.standard_delivery_time_days"
              outlined
              dense
              style="background: var(--q-white); width: 30%"
              :maxlength="9"
              :rules="[
                (val) => isEmpty(val) || $t('validation.required'),
                (val) => isNumeric(val) || $t('validation.numeric_only'),
              ]"
              lazy-rules
              class="input-area exception-right"
              input-class="text-right"
              suffix="日"
              @keydown.enter.prevent
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <label class="requier">基準納期名称</label>
            <q-input
              v-model="inputData.standard_delivery_time_name"
              outlined
              dense
              style="background: var(--q-white); width: 30%"
              class="input-area"
              :maxlength="20"
              :rules="[(val) => isEmpty(val) || $t('validation.required')]"
              lazy-rules
              @keydown.enter.prevent
            />
          </div>
        </div>
        <div v-if="mode == modeType.MODE_EDIT" class="row">
          <div class="col column">
            <label>登録者</label>
            <p class="q-mb-none q-ml-sm q-mt-sm" style="word-break: break-all">
              {{ createUserName }}
            </p>
          </div>
          <div class="col column">
            <label>登録日時</label>
            <p class="q-mb-none q-ml-sm q-mt-sm">{{ createTimestamp }}</p>
          </div>
        </div>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div class="row">
              <q-btn
                color="grey-7"
                outline
                label="戻る"
                style="min-width: 120px; background: var(--q-white) !important"
                @click="router.replace({ name: 'deliveryTime' })"
              />
              <div class="col q-gutter-x-xs flex justify-end">
                <q-btn
                  type="submit"
                  color="primary"
                  style="width: 150px"
                  :label="mode === modeType.MODE_REGISTER ? '登録' : '変更'"
                />
                <q-btn
                  type="reset"
                  outline
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  :icon="mode === modeType.MODE_REGISTER ? 'mdi-eraser' : 'cached'"
                  :label="mode === modeType.MODE_REGISTER ? 'クリア' : 'リセット'"
                />
              </div>
            </div>
          </template>
        </FormFooterBanner>
      </div>
    </q-form>
    <AlertDialog
      :display="dialogDisplayFlg"
      :level="DIALOG_LEVEL"
      :confirmation="dialogConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="router.replace({ name: 'deliveryTime' })"
      @confirm="mode === modeType.MODE_REGISTER ? registerDeliveryTime() : editDeliveryTime()"
      @close="dialogDisplayFlg = false"
    />
  </div>
</template>
<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import { isEmpty, isNumeric } from '../../common/utils/validateUtils';

interface Props {
  mode: string;
  deliveryTimeCode?: string;
}

const router = useRouter();
// 納期一覧画面からの遷移時に取得した値(mode, deliveryTimeCode)
const props = defineProps<Props>();
// 納期一覧画面で取得した基準納期Code
const deliveryTimeCode = ref(props.deliveryTimeCode ? props.deliveryTimeCode : '');
// 変更する納期の作成ユーザー名
const createUserName = ref('');
// 変更する納期の作成ユーザーID
const createUserId = ref('');
// 変更する納期の作成日時
const createTimestamp = ref('');
// ダイアログのdisplayFlg(表示判断)
const dialogDisplayFlg = ref(false);
// ダイアログのlevel(informationで固定)
const DIALOG_LEVEL = 'information';
// ダイアログのtitle
const dialogTitle = ref('');
// ダイアログのtext(配列にすることでXSSの対策を行う)
const dialogText = ref(['']);
// ダイアログのconfirmation(trueならキャンセルボタンを表示しない)
const dialogConfFlg = ref(false);
// エラーメッセージ表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');

const modeType = {
  // 登録
  MODE_REGISTER: '0',
  // 編集
  MODE_EDIT: '1',
};

// リセット用データ
const resetData = reactive({
  described_number: '',
  standard_delivery_time_code: '',
  standard_delivery_time_days: '1',
  standard_delivery_time_name: '',
});

// 入力データ
const inputData = ref({ ...resetData });

/**
 * エラー初期化
 */
const resetErrors = () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * 入力値にエラーがなければダイアログの中身を設定
 */
const submit = () => {
  if (props.mode === modeType.MODE_REGISTER) {
    dialogTitle.value = '登録確認';
    dialogText.value = ['入力された内容で登録します。', 'よろしいですか？'];
  } else {
    dialogTitle.value = '更新確認';
    dialogText.value = ['入力された内容で更新します。', 'よろしいですか？'];
  }
  resetErrors();
  dialogDisplayFlg.value = true;
};

/**
 * クリア、リセット
 */
const reset = () => {
  resetErrors();
  inputData.value = { ...resetData };
};

/**
 * 基準納期情報取得処理(更新画面の初期表示用)
 */
const getDeliveryTime = async () => {
  resetErrors();
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('standard_delivery_time_code', deliveryTimeCode.value);

  // 基準納期情報を取得するController呼び出し
  await axios
    .post('/cmd005102/getDeliveryTime', formData)
    .then((res) => {
      const { common, deliveryTimeData } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          Object.assign(resetData, deliveryTimeData);
          // Integer型のデータをString型に変換
          resetData.described_number = String(deliveryTimeData.described_number);
          resetData.standard_delivery_time_days = String(
            deliveryTimeData.standard_delivery_time_days
          );
          inputData.value = { ...resetData };

          // 更新時用に作成ユーザー名、作成ユーザーID、作成日時をフィールドにセット
          createUserName.value = deliveryTimeData.create_user_name;
          createUserId.value = String(deliveryTimeData.create_user_id);
          createTimestamp.value = deliveryTimeData.create_timestamp;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 基準納期登録処理
 */
const registerDeliveryTime = async () => {
  resetErrors();
  // Loading Start
  Loading.show();

  const formData = new FormData();
  formData.append('standard_delivery_time_code', inputData.value.standard_delivery_time_code);
  formData.append('standard_delivery_time_days', inputData.value.standard_delivery_time_days);
  formData.append('standard_delivery_time_name', inputData.value.standard_delivery_time_name);
  formData.append('described_number', inputData.value.described_number);

  // 基準納期登録を行うController呼び出し
  await axios
    .post('/cmd005101/register', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 登録確認ダイアログのtitleを設定
          dialogTitle.value = '登録完了';
          // 登録確認ダイアログのtextを生成
          dialogText.value = [
            `${inputData.value.standard_delivery_time_code} の登録が完了しました。`,
          ];
          // ダイアログのボタン数判断をtrue(1つ)に
          dialogConfFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.INSERT_ERROR:
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            case DetailResultStatusCode.DUPLICATE_DELIVERY_TIME_ERROR:
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
              router.push({ name: 'error' });
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 基準納期変更処理
 */
const editDeliveryTime = async () => {
  resetErrors();
  // Loading Start
  Loading.show();

  const formData = new FormData();
  formData.append('standard_delivery_time_code', inputData.value.standard_delivery_time_code);
  formData.append('standard_delivery_time_days', inputData.value.standard_delivery_time_days);
  formData.append('standard_delivery_time_name', inputData.value.standard_delivery_time_name);
  formData.append('described_number', inputData.value.described_number);
  formData.append('create_user_id', createUserId.value);
  formData.append('create_timestamp', createTimestamp.value);
  formData.append('beforeEditDeliveryTimeCode', deliveryTimeCode.value);

  // 基準納期変更を行うController呼び出し
  await axios
    .post('/cmd005102/edit', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 変更確認ダイアログのtitleを設定
          dialogTitle.value = '更新完了';
          // 変更確認ダイアログのtextを生成
          dialogText.value = [
            `${inputData.value.standard_delivery_time_code} の更新が完了しました。`,
          ];
          // ダイアログのボタン数判断をtrue(1つ)に
          dialogConfFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.UPDATE_ERROR:
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            case DetailResultStatusCode.DUPLICATE_DELIVERY_TIME_ERROR:
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              // メッセージチップにエラーメッセージを表示
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
              router.push({ name: 'error' });
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// propsが存在しない場合
if (!props.mode) {
  // 前画面に遷移
  router.replace({ name: 'deliveryTime' });
} else if (props.mode === modeType.MODE_EDIT) {
  getDeliveryTime();
}
</script>
<style scoped>
.cmd00510-input-form {
  width: 70%;
  margin: 0 auto;
}
</style>
<style>
.cmd00510-input-form .input-area.q-field.exception-right .q-field__native {
  padding-right: 34px;
}

.cmd00510-input-form .input-area.q-field.exception-right.q-field--error .q-field__native {
  padding-right: 60px;
}

.cmd00510-input-form .input-area.q-field.exception-right .q-field__suffix {
  position: absolute;
  right: 12px;
}

.cmd00510-input-form .input-area.q-field.exception-right.q-field--error .q-field__suffix {
  position: absolute;
  right: 40px;
}
</style>
